import React, { useState, useEffect, useRef, useCallback } from "react";
import ReactDOM from "react-dom/client";
import { openDialog } from "./Application";
import { t } from "./i18n";
import { antiforgeryHeaders } from "./Csrf";

function getCompetitionContext() {
    const parameters = new URLSearchParams(window.location.search);
    const flow = parameters.get("flow");
    const communityId = Number(parameters.get("communityId"));

    return {
        isCompetitionFlow: flow === "competition" && Number.isFinite(communityId) && communityId > 0,
        communityId,
    };
}

function getUiLanguage() {
    return (document.documentElement.lang || "es").toLowerCase();
}

function createTopUpActionError(message) {
    const language = getUiLanguage();
    const error = new Error(message);
    error.actionUrl = `/${language}/payment?balance=true`;
    error.actionText = t("Top up balance");
    return error;
}

function getStoryProgressStorageKey(userId, storyId) {
    const normalizedUserId = (userId || "").trim();
    const normalizedStoryId = String(storyId || "").trim();
    if (!normalizedUserId || !normalizedStoryId) {
        return null;
    }

    return `story-progress:${normalizedUserId}:${normalizedStoryId}`;
}

function readStoredStoryProgress(storageKey) {
    if (!storageKey) {
        return null;
    }

    try {
        const raw = window.localStorage.getItem(storageKey);
        if (!raw) {
            return null;
        }

        const parsed = JSON.parse(raw);
        if (parsed?.sceneId == null) {
            return null;
        }

        return {
            sceneId: String(parsed.sceneId),
            sourceSceneId: parsed.sourceSceneId == null ? null : String(parsed.sourceSceneId),
            choiceIndex: parsed.choiceIndex,
            feedback: parsed.feedback == null ? null : String(parsed.feedback),
            color: parsed.color == null ? null : String(parsed.color),
        };
    } catch {
        return null;
    }
}

function writeStoredStoryProgress(storageKey, sceneId, choice) {
    if (!storageKey || sceneId == null) {
        return;
    }

    try {
        window.localStorage.setItem(storageKey, JSON.stringify({
            sceneId: String(sceneId),
            sourceSceneId: choice?.sourceSceneId ? String(choice.sourceSceneId) : null,
            choiceIndex: choice?.choiceIndex != null ? choice.choiceIndex : null,
            feedback: choice?.feedback == null ? null : String(choice.feedback),
            color: choice?.color == null ? null : String(choice.color),
            updated: new Date().toISOString()
        }));
    } catch {
    }
}

// On resume the original choice object is not kept; the feedback is
// reconstructed from localStorage (the decision made on this device) or,
// as a fallback, from the single story decision that leads to the
// resumed scene. If several decisions reach it, it is not guessed.
function restoreResumedChoice(storyData, resumedScene, storedProgress) {
    if (storedProgress && storedProgress.sceneId === String(resumedScene.id)) {
        if (storedProgress.sourceSceneId != null && storedProgress.choiceIndex != null) {
            const sourceScene = storyData.scenes.find(s => String(s.id) === String(storedProgress.sourceSceneId));
            if (sourceScene && sourceScene.choices && sourceScene.choices[storedProgress.choiceIndex]) {
                const choice = sourceScene.choices[storedProgress.choiceIndex];
                return { ...choice, sourceSceneId: sourceScene.id, choiceIndex: storedProgress.choiceIndex };
            }
        }
    }

    const inboundChoices = [];
    for (const scene of storyData.scenes) {
        let cIndex = 0;
        for (const choice of scene.choices || []) {
            if (String(choice.next) === String(resumedScene.id)) {
                inboundChoices.push({ ...choice, sourceSceneId: scene.id, choiceIndex: cIndex });
            }
            cIndex++;
        }
    }

    if (inboundChoices.length === 1) {
        return inboundChoices[0];
    }

    if (storedProgress && storedProgress.sceneId === String(resumedScene.id) && storedProgress.feedback) {
        // Legacy rescue: If we can't identify the exact choice (missing sourceSceneId and multiple inbound),
        // we can try to match the color to at least return a translated choice instead of the stuck language string.
        const matchingChoices = inboundChoices.filter(c => String(c.color) === String(storedProgress.color));
        if (matchingChoices.length > 0) {
            return matchingChoices[0];
        }

        // Ultimate fallback
        return { feedback: storedProgress.feedback, color: storedProgress.color || undefined };
    }

    return null;
}

function normalizeRemainingSeconds(value) {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) {
        return 0;
    }
    return Math.max(0, Math.min(1200, Math.round(parsed)));
}

function formatRemainingTime(totalSeconds) {
    const seconds = Math.max(0, Number(totalSeconds) || 0);
    const minutesPart = Math.floor(seconds / 60).toString().padStart(2, "0");
    const secondsPart = (seconds % 60).toString().padStart(2, "0");
    return `${minutesPart}:${secondsPart}`;
}

// Autoplay blocked by the browser (NotAllowedError, e.g. when arriving via the
// resume redirect without a prior gesture): retries playback on the
// user's first interaction. Returns a function to cancel the retry.
function retryPlaybackOnFirstInteraction(play) {
    const handler = () => {
        cancel();
        play();
    };
    const cancel = () => {
        document.removeEventListener("pointerdown", handler);
        document.removeEventListener("keydown", handler);
    };
    document.addEventListener("pointerdown", handler);
    document.addEventListener("keydown", handler);
    return cancel;
}

function createEmailCaptureError(communityId, storyUrl) {
    const error = new Error(t("You need an account to continue."));
    error.emailCapture = { community: communityId, url: storyUrl };
    return error;
}

async function buyStoryAccess(storyId, communityId, storyUrl) {
    const response = await fetch("/api/story-access/buy", {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            "Accept": "application/json",
            ...antiforgeryHeaders()
        },
        credentials: "same-origin",
        body: JSON.stringify({ storyId, communityId })
    });

    if (!response.ok) {
        let body = null;
        try {
            body = await response.json();
        } catch {
        }
        const message = body?.message || t("The payment could not be processed.");

        if (body?.requiredCredits != null) {
            throw createTopUpActionError(message);
        }

        // No session: capture the email to send the signup link and
        // resume the purchase after topping up balance.
        if (response.status === 401) {
            throw createEmailCaptureError(communityId, storyUrl);
        }

        // Generic failure with an active session: offer the balance top-up.
        if (!body?.message) {
            throw createTopUpActionError(message);
        }

        throw new Error(message);
    }

    return response.json();
}

async function getNextCompetitionStory(communityId) {
    const response = await fetch(`/api/competition/community/${communityId}/play`, {
        method: "POST",
        credentials: "same-origin",
        headers: {
            "Accept": "application/json",
            ...antiforgeryHeaders()
        }
    });

    if (!response.ok) {
        throw new Error(t("The next story could not be loaded."));
    }

    const payload = await response.json();
    if (!payload?.story?.url || !payload?.story?.id) {
        throw new Error(t("There is no story available to continue."));
    }

    return payload.story;
}

async function getPersistedSceneProgress(storyId) {
    try {
        const response = await fetch(`/api/participation/progress/${storyId}`, {
            method: "GET",
            credentials: "same-origin",
            headers: {
                "Accept": "application/json"
            }
        });

        if (!response.ok) {
            return null;
        }

        const payload = await response.json();
        return payload?.sceneId == null ? null : String(payload.sceneId);
    } catch {
        return null;
    }
}

function Choice({ choices, onChoiceSelected, disabled }) {
    if (!choices || choices.length === 0) return null;
    return (
        <div>
            {choices.map((choice, index) => (
                <React.Fragment key={index}>
                    <button
                        onClick={() => onChoiceSelected(choice)}
                        disabled={disabled}
                    >
                        {choice.text}
                    </button>
                    <span> </span>
                </React.Fragment>
            ))}
        </div>
    );
}

function Story({ id = 1, userId = "", hasAccess = true, communityId = "" }) {
    const [story, setStory] = useState(null);
    const [current, setCurrent] = useState(null);
    const [selected, setSelected] = useState(null);
    const [storyError, setStoryError] = useState(null);
    const [score, setScore] = useState(0);
    const [isFirstScene, setIsFirstScene] = useState(true);

    const [sceneVisible, setSceneVisible] = useState(false);
    const [sceneDelayActive, setSceneDelayActive] = useState(true);

    const [background, setBackground] = useState({
        current: "",
        next: "",
        transitioning: false,
    });

    const [videoActive, setVideoActive] = useState(false);
    const [videoJustEnded, setVideoJustEnded] = useState(false);
    const [imageJustChanged, setImageJustChanged] = useState(false);

    const [audioEnabled, setAudioEnabled] = useState(true);
    const [isAudioPlaying, setIsAudioPlaying] = useState(false); // Tracks if scene audio is actively playing
    const [musicEnabled, setMusicEnabled] = useState(true);
    const [isTextVisible, setIsTextVisible] = useState(true);
    const [continueLoading, setContinueLoading] = useState(false);
    const [competitionContext] = useState(() => getCompetitionContext());
    const [competitionStatus, setCompetitionStatus] = useState({
        hasActiveCompetition: false,
        remainingSeconds: 0,
        currentUserScore: null,
        currentUserRank: null,
        maxScore: 0,
        competitionBalance: 0,
    });
    const progressStorageKey = getStoryProgressStorageKey(userId, id);

    const fetchCompetitionStatus = useCallback(async () => {
        if (!competitionContext.isCompetitionFlow) {
            return;
        }

        try {
            const response = await fetch(`/api/competition/community/${competitionContext.communityId}/status`, {
                method: "GET",
                credentials: "same-origin",
                headers: {
                    "Accept": "application/json"
                }
            });

            if (!response.ok) {
                return;
            }

            const payload = await response.json();

            setCompetitionStatus((prev) => ({
                ...prev,
                hasActiveCompetition: Boolean(payload?.hasActiveCompetition),
                remainingSeconds: normalizeRemainingSeconds(payload?.remainingSeconds),
                currentUserScore: payload?.currentUserScore == null ? null : Number(payload.currentUserScore),
                currentUserRank: payload?.currentUserRank == null ? null : Number(payload.currentUserRank),
                maxScore: Number.isFinite(Number(payload?.maxScore)) ? Number(payload.maxScore) : 0,
                competitionBalance: Number.isFinite(Number(payload?.competitionBalance)) ? Number(payload.competitionBalance) : 0,
            }));
        } catch {
        }
    }, [competitionContext.isCompetitionFlow, competitionContext.communityId]);

    const videoContainerRef = useRef(null);
    const audioRef = useRef(null);
    const musicRef = useRef(null); // Ref for the music audio element

    const showSceneCard = () => {
        setSceneVisible(true);
        setSceneDelayActive(false);
        // If the scene has audio, the text should NOT be visible initially ONLY if the user has not shown it before.
        // Si NO tiene audio, el texto SÍ debe ser visible inicialmente.
        if (!isTextVisible) {
            setIsTextVisible(false);
        } else {
            setIsTextVisible(true);
        }
        // If isTextVisible is true, it stays true (the user already showed it)
    };

    // Function to toggle the text's visibility
    const toggleTextVisibility = () => {
        setIsTextVisible(prev => !prev);
    };

    const handleVideoEnd = (nextImage) => {
        if (document.fullscreenElement) {
            document.exitFullscreen().catch(err => console.log('Could not exit full screen:', err));
        }
        setVideoActive(false);
        setVideoJustEnded(true);
        setImageJustChanged(false);
        if (nextImage) {
            setBackground({
                current: nextImage,
                next: "",
                transitioning: false,
            });
        }
        const nextImageIsJpg = nextImage.toLowerCase().endsWith('.jpg');
        if (!nextImageIsJpg) {
            showSceneCard();
        }
    };

    useEffect(() => {
        setStory(null);
        setCurrent(null);
        setStoryError(null);
        setScore(0);
        setIsFirstScene(true);
        setSceneVisible(false);
        setSceneDelayActive(true);
        setIsTextVisible(true);
        setBackground({ current: "", next: "", transitioning: false });
        setVideoActive(false);
        setVideoJustEnded(false);
        setImageJustChanged(false);
        setIsAudioPlaying(false);
        setContinueLoading(false);

        // Without access (story not purchased) no content is loaded: the render
        // shows the balance warning with the link to top up.
        if (!hasAccess) {
            return;
        }

        const loadStory = async () => {
            try {
                // Scenes by language: story/{id}.{lang}.json generated by the content
                // translation; Spanish is the canonical story/{id}.json and the fallback.
                const language = (document.documentElement.lang || "es").toLowerCase();
                let response = await fetch(`/community/${communityId}/${id}.${language}.json`);
                if (!response.ok) {
                    response = await fetch(`/community/${communityId}/${id}.es.json`);
                }
                if (!response.ok) {
                    throw new Error(`${t("Error loading story")} ${id}: ${response.status} ${response.statusText}`);
                }

                const storyData = await response.json();

                if (!storyData.scenes || !Array.isArray(storyData.scenes) || storyData.scenes.length === 0) {
                    throw new Error(t("Invalid story format: the scenes array was not found or is empty."));
                }

                const persistedSceneId = await getPersistedSceneProgress(id);
                const storedProgress = readStoredStoryProgress(progressStorageKey);
                const resumeSceneId = persistedSceneId || storedProgress?.sceneId || null;
                const resumedScene = resumeSceneId
                    ? storyData.scenes.find((scene) => String(scene.id) === resumeSceneId)
                    : null;

                const start = resumedScene || storyData.scenes[0];
                const initialImage = start.image ? `/community/${communityId}/${start.image}` : "";
                const initialImageIsJpg = initialImage.toLowerCase().endsWith('.jpg');

                setStory(storyData);
                setCurrent(start);
                setIsFirstScene(!resumedScene);
                setSelected(resumedScene
                    ? restoreResumedChoice(storyData, resumedScene, storedProgress)
                    : null);
                setBackground({ current: initialImage, next: "", transitioning: false });
                setStoryError(null);

                if (start.video) {
                    setVideoActive(true);
                } else if (!initialImageIsJpg) {
                    showSceneCard();
                } else if (resumedScene) {
                    // On resume, isFirstScene stays false and the initial zoom does not
                    // run; marking the image as just changed triggers the same
                    // animation whose onAnimationEnd shows the scene card.
                    setImageJustChanged(true);
                }

            } catch (err) {
                console.error('Error loading story:', err);
                setStoryError(err.message);
            }
        };

        loadStory();
    }, [id, progressStorageKey, hasAccess]);

    useEffect(() => {
        if (!current) {
            return;
        }

        // selected is always assigned before current, so here it already reflects
        // the decision that led to the current scene; it is omitted from the dependencies
        // to write only when the scene changes.
        writeStoredStoryProgress(progressStorageKey, current.id, selected);
    }, [current, progressStorageKey]);

    useEffect(() => {
        if (videoActive && videoContainerRef.current) {
            setTimeout(() => {
                const element = videoContainerRef.current;
                const requestFullScreen = element.requestFullscreen ||
                    element.webkitRequestFullscreen ||
                    element.mozRequestFullScreen ||
                    element.msRequestFullscreen;

                if (requestFullScreen) {
                    requestFullScreen.call(element).catch(err => {
                        console.error("Failed to enter full screen. Browser restrictions may apply.", err);
                    });
                }
            }, 500);
        }
    }, [videoActive]);

    useEffect(() => {
        if (!competitionContext.isCompetitionFlow) {
            return;
        }

        fetchCompetitionStatus();
        const refreshInterval = window.setInterval(fetchCompetitionStatus, 20000);

        return () => {
            window.clearInterval(refreshInterval);
        };
    }, [competitionContext.isCompetitionFlow, fetchCompetitionStatus]);

    useEffect(() => {
        if (!competitionContext.isCompetitionFlow) {
            return;
        }

        const timer = window.setInterval(() => {
            setCompetitionStatus((prev) => ({
                ...prev,
                remainingSeconds: prev.remainingSeconds > 0 ? prev.remainingSeconds - 1 : 0,
            }));
        }, 1000);

        return () => {
            window.clearInterval(timer);
        };
    }, [competitionContext.isCompetitionFlow]);

    useEffect(() => {
        if (!current || !audioRef.current || videoActive) return;

        const audio = audioRef.current;
        const newSrc = current.audio ? `/community/${communityId}/${current.audio}` : "";
        const fullNewSrc = newSrc ? new URL(newSrc, window.location.origin).href : "";

        const handlePlay = () => setIsAudioPlaying(true);
        const handlePause = () => setIsAudioPlaying(false);
        const handleEnded = () => setIsAudioPlaying(false);

        audio.addEventListener('play', handlePlay);
        audio.addEventListener('pause', handlePause);
        audio.addEventListener('ended', handleEnded);

        audio.volume = 0.9;

        let disposed = false;
        let cancelPlayRetry = null;

        // This block ensures automatic playback when the scene card appears (sceneVisible becomes true)
        if (sceneVisible && !sceneDelayActive) {
            if (newSrc && audio.src !== fullNewSrc) {
                audio.src = newSrc;
                audio.load();
            }

            if (audioEnabled && newSrc) {
                // IMPORTANT: Automatically try to play when scene is ready and audio is enabled.
                if (audio.paused) {
                    audio.play().catch(() => {
                        if (!disposed) {
                            cancelPlayRetry = retryPlaybackOnFirstInteraction(() => {
                                audio.play().catch(err => console.error('Error playing audio after scene visible:', err));
                            });
                        }
                    });
                }
            } else if (audio.src) {
                audio.pause();
            }
        } else {
            // Scene is not visible or delayed (during transition), pause and reset
            if (audio.src) {
                audio.pause();
                audio.currentTime = 0;
            }
        }

        // Cleanup event listeners
        return () => {
            disposed = true;
            if (cancelPlayRetry) {
                cancelPlayRetry();
            }
            audio.removeEventListener('play', handlePlay);
            audio.removeEventListener('pause', handlePause);
            audio.removeEventListener('ended', handleEnded);
        };
    }, [current, audioEnabled, sceneVisible, sceneDelayActive, videoActive]);

    useEffect(() => {
        if (!story || !musicRef.current) return;

        const music = musicRef.current;
        const musicSrc = story.music ? `/community/${communityId}/${story.music}` : "";
        const fullMusicSrc = musicSrc ? new URL(musicSrc, window.location.origin).href : "";

        music.loop = true;
        music.volume = 0.1;

        if (musicSrc && music.src !== fullMusicSrc) {
            music.src = musicSrc;
            music.load();
        }

        let disposed = false;
        let cancelPlayRetry = null;

        if (musicEnabled && musicSrc) {
            music.play().catch(() => {
                if (!disposed) {
                    cancelPlayRetry = retryPlaybackOnFirstInteraction(() => {
                        music.play().catch(err => console.error('Error playing background music:', err));
                    });
                }
            });
        } else {
            music.pause();
        }

        return () => {
            disposed = true;
            if (cancelPlayRetry) {
                cancelPlayRetry();
            }
            if (musicRef.current) {
                musicRef.current.pause();
            }
        };

    }, [story, musicEnabled]);

    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.hidden) {
                if (audioRef.current) {
                    audioRef.current.pause();
                }
                if (musicRef.current) {
                    musicRef.current.pause();
                }
            } else {
                if (musicEnabled && musicRef.current && musicRef.current.src) {
                    musicRef.current.play().catch(err => console.error('Error resuming background music:', err));
                }
                if (audioEnabled && audioRef.current && audioRef.current.src && sceneVisible && !sceneDelayActive && !videoActive) {
                    audioRef.current.play().catch(err => console.error('Error resuming audio:', err));
                }
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [audioEnabled, musicEnabled, sceneVisible, sceneDelayActive, videoActive]);

    const nodeMap = {};
    if (story && story.scenes) {
        story.scenes.forEach((node) => {
            nodeMap[node.id] = node;
        });
    }

    const handleChoiceClick = (choice) => {
        // compute new score synchronously so we can persist it.
        // Each choice carries a graded value (1-3) by decision quality;
        // the score adds that value, not a flat point.
        let newScore = score;
        let scoreIncrement = 0;
        if (choice.value !== undefined) {
            scoreIncrement = Math.max(0, Math.min(3, Math.round(Number(choice.value) || 0)));
            newScore = score + scoreIncrement;
            setScore(newScore);
        }
        setSelected({
            ...choice,
            sourceSceneId: current.id,
            choiceIndex: current.choices.indexOf(choice)
        });
        setIsFirstScene(false);
        window.scrollTo({ top: 0, behavior: "smooth" });
        setSceneVisible(false);
        setSceneDelayActive(true);
        setVideoJustEnded(false);
        setImageJustChanged(false);
        // We do not reset isTextVisible here to keep the user's preference

        // **Audio control is left to the useEffect.**
        // When current/scene states change, the audio useEffect will pause the old audio
        // and then, when showSceneCard() runs, it will load and automatically play the new audio.

        const nextSceneNode = nodeMap[choice.next];
        const nextImageFileName = nextSceneNode?.image || "";
        if (nextSceneNode?.video) {
            setVideoActive(true);
            setCurrent(nextSceneNode);
            setStoryError(null);
            return;
        }
        const nextImageIsJpg = nextImageFileName.toLowerCase().endsWith('.jpg');
        const delayForChange = nextImageIsJpg ? 500 : 0;
        const delayForZoom = nextImageIsJpg ? 500 : 0;
        const nextSceneHasImage = nextImageFileName !== "";
        const shouldWaitForZoom = nextImageIsJpg && nextSceneHasImage;

        setTimeout(() => {
            try {
                const nextScene = nodeMap[choice.next];
                const newNextImage = nextScene.image ? `/community/${communityId}/${nextScene.image}` : "";
                const isImageChanging = newNextImage !== background.current;
                if (isImageChanging) {
                    setCurrent(nextScene);
                    setBackground((prev) => ({
                        current: prev.current,
                        next: newNextImage,
                        transitioning: true
                    }));
                    setImageJustChanged(true);
                    setTimeout(() => {
                        setBackground({
                            current: newNextImage,
                            next: "",
                            transitioning: false
                        });
                        setStoryError(null);
                        if (!shouldWaitForZoom) {
                            showSceneCard();
                        }
                    }, delayForZoom);
                } else {
                    setCurrent(nextScene);
                    setStoryError(null);
                    showSceneCard();
                }
            } catch (err) {
                setStoryError(t("Error loading the next scene."));
                setSceneDelayActive(false);
                console.error(err);
            }
        }, delayForChange);

        // persist progress (best-effort)
        try {
            saveProgress(scoreIncrement, nextSceneNode?.id);
        } catch { }
    };

    // Persist progress for authenticated users. Fire-and-forget; failures shouldn't block UX.
    const saveProgress = async (scoreIncrement, sceneId) => {
        const normalizedScore = Number.isFinite(Number(scoreIncrement)) && Number(scoreIncrement) > 0
            ? Number(scoreIncrement)
            : 0;
        const normalizedSceneId = sceneId == null ? "" : String(sceneId).trim();

        if (normalizedScore <= 0 && !normalizedSceneId) {
            return;
        }

        try {
            await fetch('/api/participation/progress', {
                method: 'POST',
                credentials: 'same-origin',
                headers: {
                    'Content-Type': 'application/json',
                    ...antiforgeryHeaders()
                },
                body: JSON.stringify({ storyId: id, score: normalizedScore, sceneId: normalizedSceneId || null })
            });
            await fetchCompetitionStatus();
        } catch (err) {
            // ignore network errors; progress will persist when possible
            console.error('Error saving progress:', err);
        }
    };

    const resetStory = () => {
        setSceneVisible(false);
        setSceneDelayActive(true);
        setIsTextVisible(true);
        if (document.fullscreenElement) {
            document.exitFullscreen().catch(err => console.log('Could not exit full screen on reset:', err));
        }
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
            setIsAudioPlaying(false); // Reset audio playing state
        }
        const start = story.scenes[0];
        const initialImage = start.image ? `/community/${communityId}/${start.image}` : "";
        const initialImageIsJpg = initialImage.toLowerCase().endsWith('.jpg');
        if (start) {
            setCurrent(start);
            setSelected(null);
            setStoryError(null);
            setScore(0);
            setIsFirstScene(true);
            setBackground({ current: initialImage, next: "", transitioning: false });
            setVideoJustEnded(false);
            setImageJustChanged(false);
            if (start.video) {
                setVideoActive(true);
            } else if (!initialImageIsJpg) {
                showSceneCard();
            }
        } else {
            setStoryError(t("Error restarting: initial node not found."));
        }
    };

    const toggleAudioPlayPause = () => {
        // Toggling audioEnabled will pause/play in the audio useEffect
        setAudioEnabled(prev => !prev);
    };

    const isStoryFinished = current?.choices?.length === 0;

    const promptCompetitionContinuation = async () => {
        if (!competitionContext.isCompetitionFlow || continueLoading) {
            return;
        }

        setContinueLoading(true);

        try {
            const nextStory = await getNextCompetitionStory(competitionContext.communityId);

            openDialog(
                "warning",
                {
                    title: nextStory?.title || t("Next story"),
                    credits: Number(nextStory?.price) || 0,
                    onConfirm: async () => {
                        await buyStoryAccess(nextStory.id, competitionContext.communityId, nextStory.url);
                        window.location.href = nextStory.url;
                    }
                },
                "",
                "small"
            );
        } catch (err) {
            alert(err?.message || t("The competition could not be continued."));
        } finally {
            setContinueLoading(false);
        }
    };

    if (storyError) {
        return (
            <div className="card medium center animated slide">
                <h2>{t("Error loading story")}</h2>
                <p>{storyError}</p>
                <button onClick={() => window.location.reload()}>{t("Try again")}</button>
            </div>
        );
    }

    if (!hasAccess) {
        const language = (document.documentElement.lang || "es").toLowerCase();
        const topUpUrl = `/${language}/payment?balance=true`;
        return (
            <div className="card medium center animated slide">
                <h2>{t("Insufficient balance")}</h2>
                <p>{t("You need to top up your balance to access this story.")}</p>
                <a className="button" href={topUpUrl}>{t("Top up balance")}</a>
            </div>
        );
    }

    if (!story || !current) return null;

    const postVideoNextImage = current?.image ? `/community/${communityId}/${current.image}` : "";
    const isVideoScene = current.video && videoActive;
    // Scenes with their own video (generated mp4, e.g. by the Animation module,
    // referenced with a timestamp ?v=) are played with <video>; a value without
    // .mp4 is still a YouTube id.
    const isFileVideo = typeof current.video === "string" && /\.mp4(\?|$)/i.test(current.video);
    const fileVideoSrc = isFileVideo
        ? (current.video.startsWith("/") || /^https?:/i.test(current.video)
            ? current.video
            : `/community/${communityId}/${current.video}`)
        : "";
    const currentImageFileName = background.current.split('/').pop() || "";
    const currentImageIsJpg = currentImageFileName.toLowerCase().endsWith('.jpg');
    const shouldRunZoom =
        currentImageIsJpg && !background.transitioning && !isVideoScene &&
        (isFirstScene || videoJustEnded || imageJustChanged);

    // DETERMINE WHETHER THE CURRENT SCENE HAS AUDIO
    const currentSceneHasAudio = !!current.audio;


    return (
        <>
            <div
                style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    overflow: "hidden",
                    zIndex: -1
                }}
            >
                {background.current && !isVideoScene && (
                    <div
                        className="background-image"
                        key={background.current}
                        style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            backgroundImage: `url(${background.current})`,
                            backgroundSize: "cover",
                            backgroundPosition: "center",
                            backgroundRepeat: "no-repeat",
                            filter: background.transitioning || videoActive
                                ? "blur(6px) hue-rotate(45deg) saturate(1.2) contrast(1.2)"
                                : "none",
                            opacity: background.transitioning ? 0.5 : 1,
                            animation: shouldRunZoom ? (sceneVisible ?
                                "movable 25s infinite alternate" : "zoom 3s forwards")
                                : background.transitioning
                                    ? "glitch 2s steps(2,end) infinite"
                                    : "none"
                        }}
                        onAnimationEnd={(e) => {
                            if (e.animationName === 'zoom') {
                                if (currentImageIsJpg && !sceneVisible && sceneDelayActive) {
                                    showSceneCard();
                                }
                                // MINIMAL CHANGE: The following two lines were removed
                                // to allow the animation state to persist.
                                // setImageJustChanged(false);
                                // setVideoJustEnded(false);
                            }
                        }}
                    />
                )}
                {background.next && (
                    <div
                        className="background-image"
                        key={background.next}
                        style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            backgroundImage: `url(${background.next})`,
                            backgroundSize: "cover",
                            backgroundPosition: "center",
                            backgroundRepeat: "no-repeat",
                            opacity: background.transitioning ? 0.5 : 0,
                            filter: "blur(1px) hue-rotate(-45deg) saturate(1.2) contrast(1.2)",
                            animation: background.transitioning
                                ? "glitch 1s steps(2,end) infinite"
                                : "none",
                        }}
                    />
                )}
            </div>


            {current.video && videoActive && (
                <div
                    ref={videoContainerRef}
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        zIndex: 9999,
                        minHeight: '100vh',
                        minWidth: '100vw',
                    }}>
                    {isFileVideo ? (
                        <video
                            src={fileVideoSrc}
                            autoPlay
                            controls
                            playsInline
                            onEnded={() => handleVideoEnd(postVideoNextImage)}
                            style={{ width: "100%", height: "100%", objectFit: "contain", background: "#000" }}
                        />
                    ) : (
                        <iframe
                            id="youtube-player"
                            title="YouTube Video"
                            width="100%"
                            height="100%"
                            src={`https://www.youtube.com/embed/${current.video}?autoplay=1&controls=1&rel=0&enablejsapi=1&fs=1`}
                            frameBorder="0"
                            allow="autoplay; fullscreen; accelerometer; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                            onLoad={() => {
                                const listener = (event) => {
                                    if (event.data && typeof event.data === 'string' && event.origin.includes('youtube.com')) {
                                        try {
                                            const data = JSON.parse(event.data);
                                            if (data.event === 'onStateChange' && data.info === 0) { // 0 = ended
                                                handleVideoEnd(postVideoNextImage);
                                                window.removeEventListener('message', listener);
                                            }
                                        } catch { }
                                    }
                                };
                                window.addEventListener('message', listener);
                            }}
                        />
                    )}
                    <button
                        onClick={() => handleVideoEnd(postVideoNextImage)}
                        style={{
                            position: "absolute",
                            top: "50%",
                            right: "25px",
                            zIndex: 10000,
                            cursor: "pointer",
                            padding: "6px"
                        }}
                    >
                        <i className={"icon-skip-forward"}></i>
                    </button>
                </div>
            )}

            <audio ref={audioRef} />
            <audio ref={musicRef} />
            
            {!videoActive && (
                <div
                    key={current.id}
                    className={`card medium center ${sceneVisible && !sceneDelayActive ? "animated slide" : ""}`}
                    style={{
                        opacity: sceneVisible && !sceneDelayActive ? 1 : 0,
                        visibility: sceneVisible && !sceneDelayActive ? "visible" : "hidden",
                        transition: 'opacity 0.5s ease-in-out',
                        transform: sceneVisible && !sceneDelayActive ? 'none' : 'translateY(100vh)',
                        position: "relative"
                    }}
                >
                    {competitionContext.isCompetitionFlow && competitionStatus.hasActiveCompetition && (
                        <div>
                            <span>{t("Prize:")}</span> <small className="value">${(competitionStatus.competitionBalance ?? 0).toFixed(2)}</small><span> · </span>
                            <span>{t("Score:")} </span><strong className="value">{competitionStatus.currentUserScore ?? 0}</strong><span> · </span>
                            <span>{t("Position:")}</span> <strong className="value">{competitionStatus.currentUserRank ?? "-"}</strong><span> · </span>
                            <span>{t("Top score:")}</span><strong className="value">{competitionStatus.maxScore ?? 0}</strong><span> · </span>
                            <span>{t("Time:")} </span><strong className="value">{formatRemainingTime(competitionStatus.remainingSeconds)}</strong>
                            {!isFirstScene && !isStoryFinished && selected?.feedback && (
                                <p><i style={{ color: selected?.color }}>{selected?.feedback}
                                </i></p>
                            )}
                            <hr></hr>
                        </div>
                        
                    )}
                    
                    
                    {storyError && <div>⚠️ {storyError}</div>}
                    <h1>{story?.title}</h1>
                    {isFirstScene && (
                        <div>
                            <i>{story?.intro}</i>
                        </div>
                    )}
                    <div>
                        {currentSceneHasAudio && (
                            <React.Fragment>
                                <button
                                    title={isAudioPlaying ? t("Pause") : t("Play audio")}
                                    onClick={toggleAudioPlayPause}
                                >
                                    {isAudioPlaying && audioEnabled ? <i className='icon-pause'></i> : <i className='icon-play icon-1x'></i>}
                                </button>
                                <span> </span>
                            </React.Fragment>
                        )}
                        {currentSceneHasAudio && (
                            <span>
                                <button
                                    title={isTextVisible ? t("Hide text") : t("Show text")}
                                    onClick={toggleTextVisibility}
                                >
                                    {isTextVisible ? <span><i className='icon-file-text'></i><i className='icon-x'></i></span> : <i className='icon-file-text'></i>}
                                </button>
                                <span> </span>
                            </span>
                        )}
                        {story.music && (
                            <span>
                                <button
                                    title={musicEnabled ? t("Disable Music") : t("Enable Music")}
                                    onClick={() => setMusicEnabled(!musicEnabled)}
                                >
                                    {musicEnabled ? <span><i className='icon-music'></i><i className='icon-x'></i></span> : <i className='icon-music'></i>}
                                </button>
                                <span> </span>
                            </span>
                        )}



                    </div>
                   
                    <p style={{
                        visibility: (!isTextVisible) ? 'hidden' : 'visible',
                        height: (!isTextVisible) ? '0' : 'auto',
                        margin: (!isTextVisible) ? '0' : '1em 0'
                    }}>
                        <p>{current?.text}</p>
                    </p>
                                        
                    {isStoryFinished ? (
                        <div>
                            <h2>{t("THE END! 🎉")}</h2>
                            <p style={{ color: current?.color }}>{current?.feedback}</p>
                            {competitionContext.isCompetitionFlow && (
                                <button onClick={promptCompetitionContinuation} disabled={continueLoading}>
                                    {continueLoading ? t("Processing...") : t("Continue competition")}
                                </button>
                            )}
                        </div>
                    ) : (
                        <div>
                            <Choice choices={current?.choices} onChoiceSelected={handleChoiceClick} disabled={sceneDelayActive} />
                        </div>
                    )}
                </div>
            )}
        </>
    );
}

if (typeof document !== 'undefined') {
    window.addEventListener('DOMContentLoaded', () => {
        const story = document.getElementById('story');
        if (story) {
            const id = document.getElementById('id').value;
            const userId = document.getElementById('user-id')?.value || "";
            const hasAccess = document.getElementById('has-access')?.value !== "false";
            const communityId = document.getElementById('community-id')?.value || "";
            const root = ReactDOM.createRoot(story);
            root.render(
                <React.StrictMode>
                    <Story id={id} userId={userId} hasAccess={hasAccess} communityId={communityId} />
                </React.StrictMode>
            );
        }
    });
}

export default Story;
